import axios from "axios"
import { BASE_URL } from "../utils/endpoints"

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

export const useAuth = () => {
  const getAccessToken = () => {
    return document.cookie.split('; ').find(row => row.startsWith('accessToken='))?.split('=')[1]
  }

  const getRefreshToken = () => {
    return document.cookie.split("; ").find(row => row.startsWith('refreshToken'))?.split("=")[1]
  }

  const clearTokens = () => {
    const domain = window.location.hostname
    document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict; Secure'
    document.cookie = 'refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict; Secure'
    document.cookie = 'activesession=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict; Secure'
  }

  const refreshAccessToken = async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      throw new Error("No refresh token available")
    }
    const response = await axios.post(`${BASE_URL}/authentication/token/refresh/`, {
      "refresh": refreshToken
    })
    document.cookie = `accessToken=${response.data.access}; SameSite=Strict; Secure; path=/;`
    return response.data.access
  }

  const requestWithAuth = async (url, method = "GET", data = null, headers = {}) => {
    let accessToken = getAccessToken()

    try {
      if (accessToken) {
        const response = await axios({
          url: `${BASE_URL}${url}`,
          method,
          data,
          headers: {
            ...headers,
            Authorization: `JWT ${accessToken}`,
          },
        })
        return response.data
      }
      throw new Error("No access token")
    } catch (error) {
      if (error.response?.status === 401) {
        if (error.config?._retry) {
          throw error
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then(token => {
            return axios({
              url: `${BASE_URL}${url}`,
              method,
              data,
              headers: {
                ...headers,
                Authorization: `JWT ${token}`,
              },
            }).then(res => res.data)
          })
        }

        if (error.config) {
          error.config._retry = true
        }
        isRefreshing = true

        try {
          const newToken = await refreshAccessToken()
          processQueue(null, newToken)

          const retryResponse = await axios({
            url: `${BASE_URL}${url}`,
            method,
            data,
            headers: {
              ...headers,
              Authorization: `JWT ${newToken}`,
            },
          })
          return retryResponse.data
        } catch (refreshError) {
          processQueue(refreshError, null)
          clearTokens()
          window.dispatchEvent(new CustomEvent('auth:logout'))
          throw refreshError
        } finally {
          isRefreshing = false
        }
      } else {
        throw error
      }
    }
  }

  return { requestWithAuth }
}
